<style scoped>
.topbar-content {
	display: flex;
	align-items: center;
	gap: 10px;
	height: 56px;
	padding: 0 16px;
	background: #fff;
	border-bottom: 1px solid #dee2e6;
	box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
}
.planning-map {
	position: absolute;
	inset: 0;
}
</style>

<template>
	<FullLayout>
		<template #topbar>
			<div class="topbar-content">
				<i class="bi bi-strategy"></i>
				<span class="fw-semibold">Planning Workbench</span>
				<span
					v-if="loading"
					class="spinner-border spinner-border-sm text-secondary"
					role="status"
				></span>
			</div>
		</template>

		<PlanningMap
			ref="mapPane"
			:active-kind="activeLedger as 'signals' | 'leads' | 'work_orders'"
			:area="selection.area"
			:area-mode="areaMode"
			:can-paint="canPaint"
			:draft-cells="draftCells"
			:existing-lead-cells="existingLeadCells"
			:locked-res="draftRes"
			:mode="mapMode"
			:selected-signals="selectedSignalPoints"
			:version="tileVersion"
			@cell="toggleCell"
			@cells="addCells"
			@signal-at="addSignalAt"
			@select="onMapSelect"
			@clear="onMapClear"
			@area="onAreaSelection"
			@area-cancel="areaMode = null"
			@update:mode="mapMode = $event"
		/>

		<ToolRail
			side="left"
			v-model="leftTab"
			:tabs="railTabs"
			topOffset="68px"
			:bottomOffset="railBottomOffset"
			bodyWidth="400px"
		>
			<template #details>
				<LeadWorkspace
					v-if="detailMode === 'lead'"
					:draft-cells="draftCells"
					:resolution="draftResolution"
					:evidence="evidence"
					:lead="selectedLead"
					:loading-evidence="loadingEvidence"
					:saving="saving"
					:show-close="showClose"
					:staged="stagedSignals"
					@save="saveLead"
					@review="showSignalReview"
					@attach="attachSignals"
					@request-evidence="requestEvidence"
					@close="closeLead"
					@remove-cell="removeCell"
				/>
				<WorkOrderWorkspace
					v-else-if="detailMode === 'work_order'"
					:draft-cells="draftCells"
					:resolution="draftResolution"
					:order="selectedWorkOrder"
					:saving="saving"
					@save="saveWorkOrder"
					@cancel="cancelWorkOrder"
					@remove-cell="removeCell"
				/>
				<SignalDetails
					v-else-if="detailMode === 'signal' && selectedSignal"
					:signal="selectedSignal"
					:saving="reviewing !== null"
					@review="(body) => reviewSignal(selectedSignal!.id, body)"
					@forge="forgeFromSelected"
					@clear="clearSelection"
				/>
				<MultiSelectionPanel
					v-else-if="detailMode === 'multi'"
					:kind="selection.record?.kind ?? 'signal'"
					:count="selection.count"
					:saving="saving"
					@review="bulkReview"
					@forge="forgeFromSelected"
					@cancel="bulkCancelWorkOrders"
					@clear="clearSelection"
				/>
				<p v-else class="text-muted small mb-0">
					Select a row in the ledger to see its details. Use the checkboxes to
					act on several Signals or Work Orders at once.
				</p>
			</template>
			<template #legend>
				<PlanningLegend />
			</template>
			<template #selection>
				<div class="fw-semibold small mb-1">Click on the map</div>
				<div class="btn-group w-100 mb-1">
					<button
						class="btn btn-sm"
						:class="
							mapMode === 'select' ? 'btn-primary' : 'btn-outline-primary'
						"
						@click="mapMode = 'select'"
					>
						Select
					</button>
					<button
						class="btn btn-sm"
						:class="mapMode === 'paint' ? 'btn-primary' : 'btn-outline-primary'"
						:disabled="!canPaint"
						@click="mapMode = 'paint'"
					>
						Paint cells
					</button>
					<button
						class="btn btn-sm"
						:class="
							mapMode === 'signal' ? 'btn-primary' : 'btn-outline-primary'
						"
						:disabled="placingSignal"
						@click="mapMode = 'signal'"
					>
						Add signal
					</button>
				</div>
				<p class="form-text mb-2">
					Select picks the Signal, Lead, or Work Order under the pointer. Paint
					adds H3 cells to the open Lead or Work Order: click a cell to add it,
					click it again to take it back, or draw an area to fill every cell it
					covers. Add signal places a new, unreviewed Signal where you click.
				</p>
				<p v-if="mapMode === 'paint'" class="form-text mb-3">
					The grid is cut at resolution {{ draftResolution
					}}<template v-if="draftRes !== null">
						, locked by the draft's first cell</template
					>. A Lead stores one resolution, so the grid stops following the zoom
					once you have painted.
				</p>

				<div class="fw-semibold small mb-1">Draw an area</div>
				<p class="small text-muted">
					<template v-if="mapMode === 'paint'">
						Fills every H3 cell the area covers, at resolution
						{{ draftResolution }}.
					</template>
					<template v-else>
						Selects every <strong>{{ activeTabLabel }}</strong> inside the drawn
						area. Click a feature to select it; Ctrl/Meta/Shift adds.
					</template>
				</p>
				<div class="d-grid gap-2">
					<button
						class="btn btn-sm"
						:class="
							areaMode === 'rectangle' ? 'btn-primary' : 'btn-outline-primary'
						"
						@click="toggleAreaMode('rectangle')"
					>
						<i class="bi bi-bounding-box me-1"></i>Draw rectangle
					</button>
					<button
						class="btn btn-sm"
						:class="
							areaMode === 'polygon' ? 'btn-primary' : 'btn-outline-primary'
						"
						@click="toggleAreaMode('polygon')"
					>
						<i class="bi bi-pentagon me-1"></i>Draw polygon
					</button>
				</div>
				<button
					class="btn btn-sm btn-outline-secondary w-100 mt-2"
					:disabled="selection.count === 0"
					@click="clearSelection"
				>
					Clear selection
				</button>
				<!-- The escalated tier: the same drawn ring, asked of the resource
				     instead of the rendered tiles, so it also names rows the map
				     never drew. Offered only where the resource declares a spatial
				     surface at all. -->
				<button
					v-if="capability?.location"
					class="btn btn-sm btn-outline-primary w-100 mt-1"
					:disabled="selection.area === null || selectingAll"
					title="Names rows not drawn on the map"
					@click="selectAllMatching"
				>
					<i class="bi bi-geo-alt me-1"></i>Select all matching
				</button>
			</template>
		</ToolRail>

		<ToolRail
			side="right"
			v-model="rightTab"
			:tabs="createTabs"
			topOffset="68px"
			:bottomOffset="railBottomOffset"
			bodyWidth="400px"
		>
			<template #lead>
				<LeadWorkspace
					:draft-cells="draftCells"
					:resolution="draftResolution"
					:evidence="null"
					:lead="null"
					:loading-evidence="false"
					:saving="saving"
					:show-close="false"
					:staged="stagedSignals"
					@save="saveLead"
					@review="showSignalReview"
					@remove-cell="removeCell"
				/>
			</template>
			<template #work_order>
				<WorkOrderWorkspace
					:draft-cells="draftCells"
					:resolution="draftResolution"
					:order="null"
					:saving="saving"
					@save="saveWorkOrder"
					@remove-cell="removeCell"
				/>
			</template>
		</ToolRail>

		<WorkbenchLedger
			:tabs="ledgerTabs"
			:active-key="activeLedger"
			:fraction="ledgerFraction"
			:expanded="ledgerExpanded"
			:selection="ledgerSelection"
			:multi="true"
			:selected-keys="selection.multi"
			:filters="activeFilters"
			:area-label="areaLabel"
			:area-keys="selection.area ? selection.multi : null"
			@update:active-key="switchLedger"
			@select="onLedgerRowSelect"
			@toggle="onLedgerToggle"
			@clear-area="selection.clear()"
			@update:fraction="setLedgerFraction"
			@update:expanded="setLedgerExpanded"
		/>

		<ToastNotification
			:title="toastTitle"
			:message="toastMessage"
			:show="toastShow"
			@update:show="toastShow = $event"
		/>
	</FullLayout>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import type { GeoJSON } from "geojson";

import { apiClient } from "@/client";
import WorkbenchLedger from "@/components/ledger/WorkbenchLedger.vue";
import type {
	LedgerFilter,
	LedgerRow,
	LedgerSelection,
	LedgerTab,
} from "@/components/ledger/ledger";
import FullLayout from "@/components/layout/Full.vue";
import ToolRail from "@/components/panel/ToolRail.vue";
import LeadWorkspace from "@/components/planning/LeadWorkspace.vue";
import MultiSelectionPanel from "@/components/planning/MultiSelectionPanel.vue";
import PlanningLegend from "@/components/planning/PlanningLegend.vue";
import PlanningMap from "@/components/planning/PlanningMap.vue";
import SignalDetails from "@/components/planning/SignalDetails.vue";
import WorkOrderWorkspace from "@/components/planning/WorkOrderWorkspace.vue";
import ToastNotification from "@/components/ToastNotification.vue";
import { cellResolution, PLANNING_H3_RESOLUTION } from "@/lib/h3/cells";
import { outerRing, useWorkbenchSelection } from "@/map/select";
import type { MapSelectPayload, SelectId } from "@/map/select";
import type {
	LeadDetail,
	LeadEvidence,
	MapReviewCapability,
	MapReviewQueryBody,
	MapReviewQueryEnvelope,
	PlanningSignal,
	WorkOrderDetail,
} from "@/type/api";

type DetailMode =
	| "none"
	| "signal"
	| "lead"
	| "work_order"
	| "new_lead"
	| "new_work_order"
	| "multi";

const loading = ref(true);
const saving = ref(false);
const reviewing = ref<number | null>(null);
const loadingEvidence = ref(false);
const showClose = ref(false);

const signals = ref<PlanningSignal[]>([]);
const leads = ref<LeadDetail[]>([]);
const workOrders = ref<WorkOrderDetail[]>([]);
const evidence = ref<LeadEvidence | null>(null);

const selectedLead = ref<LeadDetail | null>(null);
const selectedWorkOrder = ref<WorkOrderDetail | null>(null);
const draftCells = ref<string[]>([]);
/** The resolution the draft's cells are cut at. Null while the draft is empty;
 * a Lead stores one resolution, so the grid locks to it on the first cell. */
const draftRes = ref<number | null>(null);

/** What the forms save: the locked resolution, or the default before the first
 * cell makes one. One place decides it, so the map's grid and the payload's
 * `h3_resolution` cannot disagree. */
const draftResolution = computed(
	() => draftRes.value ?? PLANNING_H3_RESOLUTION,
);

/** The saved Leads' cells, by cell index, with the state that decides whether
 * an overlap is a warning. A Lead's geography is its stored cell set, and the
 * Planning read already carries every Lead, so the map needs no second read to
 * know what is already planned. An open Lead wins over a closed one when they
 * share a cell: the warning is about work that is still live. */
const existingLeadCells = computed(() => {
	const cells = new Map<string, "open" | "closed">();
	for (const lead of leads.value) {
		for (const cell of lead.cells) {
			if (lead.state === "open" || !cells.has(cell)) {
				cells.set(cell, lead.state);
			}
		}
	}
	return cells;
});

/** The draft's geography, set whole. Cells and their resolution travel
 * together because a Lead stores a single resolution: setting the cells without
 * the resolution they were cut at is what let three files disagree about it.
 * A row that carries cells but no `h3_resolution` — the early Leads predate the
 * column — is read from the cells themselves, so opening one cuts the grid at
 * the resolution its footprint already has instead of letting a second one in. */
function setDraftCells(cells: string[], resolution: number | null): void {
	draftCells.value = [...cells];
	const first = cells[0];
	draftRes.value =
		cells.length === 0 || first === undefined
			? null
			: (resolution ?? cellResolution(first));
}

// The one selection: which row the details pane reads, which rows the ledger has
// checked, and the ring an area selection is in force over. It is a pinia
// singleton, so the workbench claims it on setup — opening Planning never
// inherits what another workbench had selected.
const selection = useWorkbenchSelection();
selection.reset();

/** The map, so a ledger row can ask it to look at a place. A one-shot camera
 * request is an act, not state: a prop would re-fire on every reload of the
 * signals it derives from. */
const mapPane = ref<InstanceType<typeof PlanningMap> | null>(null);

const newKind = ref<"lead" | "work_order" | null>(null);

const activeLedger = ref("signals");
const leftTab = ref<string | null>("details");
const rightTab = ref<string | null>(null);
const ledgerFraction = ref(0.34);
const ledgerExpanded = computed(() => ledgerFraction.value >= 0.99);
const tileVersion = ref(0);
const mapMode = ref<"select" | "paint" | "signal">("select");
const areaMode = ref<"rectangle" | "polygon" | null>(null);
const canPaint = computed(
	() =>
		newKind.value !== null ||
		selectedLead.value !== null ||
		selectedWorkOrder.value !== null,
);

// Losing the draft takes the grid away, but placing a Signal needs no draft: a
// Signal is evidence in its own right, so that mode survives.
watch(canPaint, (allowed) => {
	if (!allowed && mapMode.value === "paint") mapMode.value = "select";
});

const toastTitle = ref("");
const toastMessage = ref("");
const toastShow = ref(false);

/**
 * The active tab's capability document (OPTIONS /api/<resource>): what the
 * resource declares it can be narrowed by, and — the part this page reads —
 * whether it has a spatial surface at all. A resource without one refuses a
 * drawn ring, so "Select all matching" is not offered for it.
 */
const capability = ref<MapReviewCapability | null>(null);
/** True while a "Select all matching" read is in flight. */
const selectingAll = ref(false);

const CAPABILITY_PATH: Record<string, string> = {
	signals: "/api/signal",
	leads: "/api/lead",
	work_orders: "/api/work-order",
};

/** The selection kind each ledger tab stands for. */
const KIND_BY_TAB: Record<string, string> = {
	signals: "signal",
	leads: "lead",
	work_orders: "work_order",
};

function notify(title: string, message: string) {
	toastTitle.value = title;
	toastMessage.value = message;
	toastShow.value = true;
}

/** The documents are a pure function of the server's descriptors, so one read
 * per tab switch is enough; a tab left while its request is in flight keeps
 * whatever the tab the operator is looking at declares. */
async function loadCapability(tab: string) {
	capability.value = null;
	const path = CAPABILITY_PATH[tab];
	if (!path) return;
	try {
		const document = await apiClient.JSONOptions<MapReviewCapability>(path);
		if (activeLedger.value === tab) capability.value = document;
	} catch {
		// No document, no escalated selection: the button simply is not offered,
		// and the instant selection still works.
		if (activeLedger.value === tab) capability.value = null;
	}
}

watch(activeLedger, loadCapability, { immediate: true });

const railTabs = computed(() => [
	{
		key: "details",
		icon: "bi bi-card-text",
		title: detailMode.value === "multi" ? "Selection" : "Details",
	},
	{ key: "selection", icon: "bi bi-tools", title: "Map tools" },
	{ key: "legend", icon: "bi bi-palette", title: "Legend" },
]);

const activeTabLabel = computed(
	() =>
		ledgerTabs.value.find((tab) => tab.key === activeLedger.value)?.label ??
		"objects",
);

const createTabs = [
	{ key: "lead", icon: "bi bi-crosshair", title: "New Lead" },
	{ key: "work_order", icon: "bi bi-clipboard-check", title: "New Work Order" },
];

// Closing the right rail is closing the create session; the left details pane
// is untouched.
watch(rightTab, (tab: string | null) => {
	if (!tab) {
		newKind.value = null;
		return;
	}
	// The rail's tabs are the create entry points now that the topbar has no
	// Lead/Work Order buttons. Opening a tab starts a clean create session.
	if (tab === "lead" && newKind.value !== "lead") {
		newLead();
		return;
	}
	if (tab === "work_order" && newKind.value !== "work_order") {
		newWorkOrder();
	}
});

const railBottomOffset = computed(
	() => `calc(${(ledgerFraction.value * 100).toFixed(2)}vh + 12px)`,
);

/** A Signal the Lead form asked to review: shown in the details pane beside the
 * draft, without touching the set staged for it. Held by id rather than by row
 * so it survives the refresh a review triggers. */
const reviewSignalID = ref<number | null>(null);

/** The Signal being reviewed from the draft, or null. */
const reviewTarget = computed(
	() =>
		signals.value.find((signal) => signal.id === reviewSignalID.value) ?? null,
);

const selectedSignal = computed(
	() =>
		reviewTarget.value ??
		signals.value.find(
			(signal) =>
				selection.record?.kind === "signal" &&
				signal.id === selection.record.id,
		) ??
		null,
);

/** The Signal keys the multi-selection holds. The store owns the key format, so
 * this reads the ids back out of it rather than keeping a parallel list. */
const selectedSignalIDs = computed(() =>
	[...selection.multi]
		.filter((key) => key.startsWith("signal:"))
		.map((key) => Number(key.slice("signal:".length)))
		.filter((id) => Number.isFinite(id)),
);

const selectedSignals = computed(() => {
	const byID = new Map(signals.value.map((signal) => [signal.id, signal]));
	return selectedSignalIDs.value
		.map((id) => byID.get(id))
		.filter((signal): signal is PlanningSignal => !!signal);
});

/** Signals staged for the open Lead are exactly the Signal rows checked in the
 * ledger. A Lead plus Signals is a Lead detail with evidence staged for attach;
 * Signals alone are the multi-select surface. */
const stagedSignals = selectedSignals;

const detailMode = computed<DetailMode>(() => {
	// A review opened from the Lead form owns the pane: the draft is in the other
	// rail, and the set staged for it is deliberately left alone.
	if (reviewTarget.value !== null) return "signal";
	const kinds = [...selection.multi].map((key) => key.split(":")[0]);
	if (kinds.length === 0) return "none";
	const leads = kinds.filter((kind) => kind === "lead").length;
	const orders = kinds.filter((kind) => kind === "work_order").length;
	const signals = kinds.filter((kind) => kind === "signal").length;
	if (leads === 1 && signals === kinds.length - 1) return "lead";
	if (orders === 1 && signals === 0 && kinds.length === 1) return "work_order";
	if (signals === 1 && kinds.length === 1) return "signal";
	return "multi";
});

/** Planning's tabs carry their filter declaration as well as what the drawer
 * draws: `LedgerTab` is the table, and `filters` is what this page says the tab
 * can be narrowed by. */
interface PlanningTab extends LedgerTab {
	filters: LedgerFilter[];
}

/** An enum filter's vocabulary in the operator's sentence case — "Mosquito
 * control plan", not `mosquito_control_plan`. */
function vocabulary(
	values: readonly string[],
): { value: string; label: string }[] {
	return values.map((value) => {
		const words = value.replace(/_/g, " ");
		return {
			value,
			label: words.charAt(0).toUpperCase() + words.slice(1),
		};
	});
}

// The enum vocabularies, typed against the resources' own unions: a member the
// server stops answering is a compile error here rather than a filter that can
// never match a row.
const SIGNAL_CLASSES: NonNullable<PlanningSignal["provenance_class"]>[] = [
	"production",
	"nuisance",
	"environmental",
	"administrative",
];
const SIGNAL_EFFECTS: NonNullable<PlanningSignal["effect"]>[] = [
	"promotive",
	"suppressive",
	"contextual",
];
const REVIEW_STATES = ["reviewed", "unreviewed"] as const;
const LEAD_STATES: LeadDetail["state"][] = ["open", "closed"];
const PRIORITIES: LeadDetail["priority"][] = ["high", "normal", "low"];
const WORK_ORDER_STATES: WorkOrderDetail["state"][] = [
	"open",
	"converted",
	"cancelled",
];
const WORK_ORDER_ORIGINS: WorkOrderDetail["origin"][] = [
	"manual",
	"lead",
	"assignment",
	"review",
	"mosquito_control_plan",
	"situation",
];

/** What each tab can be narrowed by. `column` is the row's own cell index, so a
 * filter and the cells it reads are declared in one place: the facets a ledger
 * offers are the columns it prints, in the vocabulary it prints them in. */
const signalFilters: LedgerFilter[] = [
	{ key: "type", label: "Type", kind: "text", column: 1 },
	{
		key: "class",
		label: "Class",
		kind: "enum",
		column: 2,
		options: vocabulary(SIGNAL_CLASSES),
	},
	{
		key: "effect",
		label: "Effect",
		kind: "enum",
		column: 3,
		options: vocabulary(SIGNAL_EFFECTS),
	},
	{
		key: "review",
		label: "Review",
		kind: "enum",
		column: 4,
		options: vocabulary(REVIEW_STATES),
	},
];

const leadFilters: LedgerFilter[] = [
	{
		key: "state",
		label: "State",
		kind: "enum",
		column: 2,
		options: vocabulary(LEAD_STATES),
	},
	{
		key: "priority",
		label: "Priority",
		kind: "enum",
		column: 3,
		options: vocabulary(PRIORITIES),
	},
];

const workOrderFilters: LedgerFilter[] = [
	{
		key: "state",
		label: "State",
		kind: "enum",
		column: 2,
		options: vocabulary(WORK_ORDER_STATES),
	},
	{
		key: "origin",
		label: "Origin",
		kind: "enum",
		column: 3,
		options: vocabulary(WORK_ORDER_ORIGINS),
	},
	{
		key: "priority",
		label: "Priority",
		kind: "enum",
		column: 4,
		options: vocabulary(PRIORITIES),
	},
];

const ledgerTabs = computed<PlanningTab[]>(() => [
	{
		key: "signals",
		label: "Signals",
		badgeCount: signals.value.filter((signal) => !signal.addressed).length,
		columns: [
			{ key: "id", label: "Signal", width: "6rem" },
			{ key: "type", label: "Type" },
			{ key: "class", label: "Class", width: "8rem" },
			{ key: "effect", label: "Effect", width: "7rem" },
			{ key: "state", label: "Review", width: "6rem" },
			{ key: "created", label: "Created", width: "7rem" },
		],
		rows: signals.value.map(signalRow),
		filters: signalFilters,
		defaultSort: { key: "created", descending: true },
	},
	{
		key: "leads",
		label: "Leads",
		badgeCount: leads.value.length,
		columns: [
			{ key: "id", label: "Lead", width: "5rem" },
			{ key: "name", label: "Name" },
			{ key: "state", label: "State", width: "6rem" },
			{ key: "priority", label: "Priority", width: "6rem" },
			{ key: "cells", label: "Cells", align: "end", width: "5rem" },
			{ key: "signals", label: "Signals", align: "end", width: "5rem" },
			{ key: "due", label: "Due", width: "7rem" },
		],
		rows: leads.value.map(leadRow),
		filters: leadFilters,
		defaultSort: { key: "id", descending: true },
	},
	{
		key: "work_orders",
		label: "Work Orders",
		badgeCount: workOrders.value.length,
		columns: [
			{ key: "id", label: "WO", width: "5rem" },
			{ key: "description", label: "Description" },
			{ key: "state", label: "State", width: "6rem" },
			{ key: "origin", label: "Origin", width: "7rem" },
			{ key: "priority", label: "Priority", width: "6rem" },
			{ key: "cells", label: "Cells", align: "end", width: "5rem" },
			{ key: "due", label: "Due", width: "7rem" },
		],
		rows: workOrders.value.map(workOrderRow),
		filters: workOrderFilters,
		defaultSort: { key: "id", descending: true },
	},
]);

/** The active tab's declaration; the values themselves are the drawer's, per
 * tab, so a clause set on Signals never narrows Leads. */
const activeFilters = computed<LedgerFilter[]>(
	() =>
		ledgerTabs.value.find((tab) => tab.key === activeLedger.value)?.filters ??
		[],
);

/** The drawn area as the ledger's own clause: named with what it selected, so
 * the chip and the highlighted rows cannot disagree. */
const areaLabel = computed<string | null>(() =>
	selection.area === null
		? null
		: `Drawn area · ${selection.count} ${activeTabLabel.value}`,
);

/** The record as a ledger row. Ledger rows carry numeric ids, so a selection
 * naming something else — an H3 cell, once cells are selectable — highlights
 * nothing rather than being coerced into a row it might collide with. */
const ledgerSelection = computed<LedgerSelection | null>(() => {
	const record = selection.record;
	if (record === null || typeof record.id !== "number") return null;
	return { kind: record.kind, id: record.id };
});

function signalRow(signal: PlanningSignal): LedgerRow {
	return {
		kind: "signal",
		id: signal.id,
		cells: [
			`S-${signal.id}`,
			signal.type,
			signal.provenance_class ?? "—",
			signal.effect ?? "—",
			signal.addressed ? "reviewed" : "unreviewed",
			formatDate(signal.created),
		],
		sort: [
			signal.id,
			signal.type,
			signal.provenance_class ?? null,
			signal.effect ?? null,
			signal.addressed ? 1 : 0,
			signal.created,
		],
	};
}

function leadRow(lead: LeadDetail): LedgerRow {
	return {
		kind: "lead",
		id: lead.id,
		cells: [
			`#${lead.id}`,
			lead.name ?? `Lead ${lead.id}`,
			lead.state,
			lead.priority,
			String(lead.cells.length),
			String(lead.signals.length),
			lead.due_at ? formatDate(lead.due_at) : "—",
		],
		sort: [
			lead.id,
			lead.name ?? "",
			lead.state,
			lead.priority,
			lead.cells.length,
			lead.signals.length,
			lead.due_at ?? null,
		],
	};
}

function workOrderRow(order: WorkOrderDetail): LedgerRow {
	return {
		kind: "work_order",
		id: order.id,
		cells: [
			`#${order.id}`,
			order.description,
			order.state,
			order.origin,
			order.priority,
			String(order.cells.length),
			order.due_at ? formatDate(order.due_at) : "—",
		],
		sort: [
			order.id,
			order.description,
			order.state,
			order.origin,
			order.priority,
			order.cells.length,
			order.due_at ?? null,
		],
	};
}

onMounted(load);

function refreshMap() {
	// A write changes a footprint; a new `v` makes MapLibre fetch the tiles
	// again instead of serving the cached vector tile for the same bounds.
	tileVersion.value += 1;
}

async function load() {
	loading.value = true;
	try {
		const [signalPage, leadPage, orderPage] = await Promise.all([
			apiClient.JSONGet<{ signals: PlanningSignal[] }>("/api/signal", {
				params: { limit: 1000 },
			}),
			apiClient.JSONGet<{ leads: LeadDetail[] }>("/api/lead", {
				params: { limit: 1000 },
			}),
			apiClient.JSONGet<{ work_orders: WorkOrderDetail[] }>("/api/work-order", {
				params: { limit: 1000 },
			}),
		]);
		signals.value = signalPage.signals ?? [];
		leads.value = leadPage.leads ?? [];
		workOrders.value = orderPage.work_orders ?? [];
		refreshMap();
		if (selectedLead.value) {
			selectedLead.value =
				leads.value.find((lead) => lead.id === selectedLead.value?.id) ?? null;
		}
		if (selectedWorkOrder.value) {
			selectedWorkOrder.value =
				workOrders.value.find(
					(order) => order.id === selectedWorkOrder.value?.id,
				) ?? null;
		}
	} catch (e: any) {
		notify(
			"Planning data not read",
			e?.message ?? "Could not load the workbench.",
		);
	} finally {
		loading.value = false;
	}
}

function switchLedger(key: string) {
	activeLedger.value = key;
	areaMode.value = null;
	// Placing a Signal is a gesture at the map, not at this tab, so switching
	// tabs ends it rather than leaving the crosshair armed.
	if (mapMode.value === "signal") mapMode.value = "select";
	clearSelection();
	closeCreate();
}

function closeCreate() {
	newKind.value = null;
	rightTab.value = null;
}

/** The point of one Signal in the loaded list, or null when it has no place.
 * The origin is not a place: a Signal whose geometry is not a point reports no
 * location, and framing it would fly to the Gulf of Guinea. */
function signalPoint(
	id: number,
): { latitude: number; longitude: number } | null {
	const signal = signals.value.find((entry) => entry.id === id);
	if (!signal) return null;
	const { latitude, longitude } = signal.location;
	if (latitude === 0 && longitude === 0) return null;
	return { latitude, longitude };
}

/** Every selected Signal's point: the markers follow the whole set, not only the
 * row the details pane happens to read, so a multi-selection is visible as a set
 * on the map. The key is the store's `${kind}:${id}` — the one place a key is
 * built — so this reads it back rather than restating the format. */
const selectedSignalPoints = computed<
	{ latitude: number; longitude: number }[]
>(() => {
	const points: { latitude: number; longitude: number }[] = [];
	for (const key of selection.multi) {
		const separator = key.indexOf(":");
		if (separator < 0) continue;
		if (key.slice(0, separator) !== "signal") continue;
		const point = signalPoint(Number(key.slice(separator + 1)));
		if (point) points.push(point);
	}
	return points;
});

/** A ledger row click selects the row and points the camera at it: a Signal's
 * own point, or a Lead's cell footprint. Both are the ground the operator's next
 * act is about — drawing a Lead's area around a Signal, or editing the Lead's
 * cells.
 *
 * Only the ledger's row click goes through here. A click on the map already
 * selects the object under the pointer, and moving the camera then would fight
 * the operator who is looking at exactly that ground. */
function onLedgerRowSelect(row: LedgerRow, additive: boolean) {
	onLedgerSelect(row, additive);
	if (row.kind === "signal") {
		const point = signalPoint(row.id);
		if (point) mapPane.value?.focusOn(point.longitude, point.latitude);
		return;
	}
	if (row.kind === "lead") {
		const lead = leads.value.find((entry) => entry.id === row.id);
		if (lead && lead.cells.length > 0) {
			mapPane.value?.focusOnCells(lead.cells);
		}
	}
}

function onLedgerSelect(row: LedgerRow, additive: boolean) {
	selection.select(row.kind, row.id, { additive });
	if (additive) {
		// A checked row joins the set; the pane may still be staging Signals
		// onto the Lead or Work Order it has open, so the create session stays.
		openRow(row, true);
		return;
	}
	closeCreate();
	openRow(row, false);
}

function onLedgerToggle(row: LedgerRow) {
	newKind.value = null;
	const wasChecked = selection.has(row.kind, row.id);
	selection.toggle(row.kind, row.id);
	if (!wasChecked) {
		// A row that was just checked is what the pane reads, so the
		// multi-selection panel offers that type's actions rather than the
		// previous row's. The select is additive, which leaves the set the
		// checkbox wrote exactly as it is.
		selection.select(row.kind, row.id, { additive: true });
	} else if (selection.count === 0) {
		// Deselecting the last row leaves the pane nothing to read, so the
		// details workspace closes with it.
		clearSelection();
		return;
	}
	openRow(row, true);
}

function onMapClear() {
	if (newKind.value !== null) return;
	clearSelection();
}

function toggleAreaMode(mode: "rectangle" | "polygon") {
	areaMode.value = areaMode.value === mode ? null : mode;
	mapMode.value = "select";
}

function tabForKind(kind: string): string {
	return kind === "signal"
		? "signals"
		: kind === "lead"
			? "leads"
			: "work_orders";
}

/** A map click selects the object it hit and brings its ledger tab forward. The
 * selection is written even when the ledger's page does not carry the row: the
 * operator pointed at the object, and the store is what that means. */
function onMapSelect(payload: MapSelectPayload) {
	const tab = tabForKind(payload.kind);
	if (activeLedger.value !== tab) {
		// Leaving the tab takes its selection with it, so nothing swept up under
		// one type is acted on under another.
		activeLedger.value = tab;
		selection.clear();
	}
	selection.select(payload.kind, payload.id, { additive: payload.additive });
	const row = ledgerTabs.value
		.find((entry) => entry.key === tab)
		?.rows.find(
			(entry) => entry.kind === payload.kind && entry.id === payload.id,
		);
	if (row) openRow(row, payload.additive);
}

/** A drawn area selects every object of the active ledger type inside it. */
function onAreaSelection(payload: {
	kind: string;
	ids: SelectId[];
	ring: [number, number][];
}) {
	areaMode.value = null;
	activeLedger.value = tabForKind(payload.kind);
	if (payload.ids.length === 0) {
		// The ring named nothing: keeping the previous set standing would
		// describe a shape that selects what it does not contain.
		clearSelection();
		return;
	}
	selection.setArea(
		payload.ring,
		payload.ids.map((id) => `${payload.kind}:${id}`),
	);
	// The pane reads one object, so the record follows the first row swept up.
	// The select is additive precisely so it does not replace the set the ring
	// just wrote; the record is the only thing it changes.
	selection.select(payload.kind, payload.ids[0], { additive: true });
	const row = ledgerTabs.value
		.find((entry) => entry.key === activeLedger.value)
		?.rows.find(
			(entry) => entry.kind === payload.kind && entry.id === payload.ids[0],
		);
	if (row) openRow(row, false);
}

/** The id cap a selection can be carried in one go at: `queryspec`'s
 * `IdentitySetIDCap`, past which a minted set travels as its token alone. */
const SELECTION_ID_CAP = 20_000;

/**
 * The ids the drawn ring keeps on the server — the escalated half of §6: rows
 * that were never drawn, so rows the instant selection cannot name.
 *
 * Where the resource serves identity sets this is Map Review's own read: the
 * smallest page the gate admits, narrowed by the ring, minting a set whose ids
 * come back in the metadata. `/api/signal` declares no identity sets (its
 * descriptor draws rows rather than a map selection), so there the same
 * narrowing is walked through the envelope's own paging and the ids are the
 * rows'. Both answer "every row inside this ring" once; `null` means the set is
 * past the cap and cannot be ticked row by row.
 */
async function escalatedIDs(
	path: string,
	area: GeoJSON.Polygon,
	servesSets: boolean,
): Promise<number[] | null> {
	if (servesSets) {
		const answer = await apiClient.JSONQuery<
			MapReviewQueryEnvelope<{ id: number }>
		>(path, {
			// The set, not a page: one row is the smallest page the gate admits,
			// and the ids arrive in the metadata.
			limit: 1,
			location: { geometry: area },
			identity_set: { mint: true },
		} satisfies MapReviewQueryBody);
		return answer.metadata.identity_set?.ids ?? null;
	}
	const answer = await apiClient.JSONQueryAll<{ id: number }>(path, {
		location: { geometry: area },
	} satisfies MapReviewQueryBody);
	if (answer.metadata.filtered_total > SELECTION_ID_CAP) return null;
	return answer.items.map((row) => row.id);
}

/** "Select all matching": the same drawn ring, asked of the resource instead of
 * the tiles, so the selection also names rows the map never drew. */
async function selectAllMatching() {
	const area = selection.area;
	const tab = activeLedger.value;
	const kind = KIND_BY_TAB[tab];
	const path = CAPABILITY_PATH[tab];
	const servesSets = capability.value?.identity_set.supported === true;
	if (!area || !kind || !path) return;
	selectingAll.value = true;
	try {
		const ids = await escalatedIDs(path, area, servesSets);
		// A tab switched while the set was being read owns its own selection: the
		// rows this answer names belong to the tab the ring was drawn in.
		if (activeLedger.value !== tab) return;
		if (ids === null) {
			// Past the cap there is no row-by-row selection to write, and a token
			// would be a highlight rather than a selection: say so instead.
			notify(
				"Too many to select",
				"This area holds more rows than can be selected one by one. Draw a smaller one.",
			);
			return;
		}
		selection.setArea(
			outerRing(area),
			ids.map((id) => `${kind}:${id}`),
		);
	} catch (e: any) {
		notify(
			"Rows not selected",
			e?.message ?? "The area could not be matched against the server.",
		);
	} finally {
		selectingAll.value = false;
	}
}

/** A checked Signal under an open Lead must not throw the Lead away: the Lead
 * is the surface the Signals are being staged onto. A plain Signal click
 * replaces the selection, so it does clear the Lead. */
function openRow(row: LedgerRow, additive: boolean) {
	// Any ordinary selection takes the pane back from a review opened by the
	// Lead form; the form itself is untouched.
	reviewSignalID.value = null;
	evidence.value = null;
	showClose.value = false;
	if (row.kind === "signal") {
		if (!additive) {
			selectedLead.value = null;
			selectedWorkOrder.value = null;
			setDraftCells([], null);
		}
		return;
	}
	if (row.kind === "lead") {
		const lead = leads.value.find((item) => item.id === row.id) ?? null;
		selectedLead.value = lead;
		selectedWorkOrder.value = null;
		setDraftCells(lead?.cells ?? [], lead?.h3_resolution ?? null);
		return;
	}
	const order = workOrders.value.find((item) => item.id === row.id) ?? null;
	selectedWorkOrder.value = order;
	selectedLead.value = null;
	setDraftCells(order?.cells ?? [], order?.h3_resolution ?? null);
}

function clearSelection() {
	selection.clear();
	reviewSignalID.value = null;
	selectedLead.value = null;
	selectedWorkOrder.value = null;
	setDraftCells([], null);
	evidence.value = null;
	showClose.value = false;
}

/** Replace what the ledger has checked with exactly these Signals — what a
 * create session starts from. The keys are the store's to build; this only says
 * which rows are staging, and the record follows the first so the pane reads a
 * Signal. */
function stageSignals(ids: number[]) {
	selection.clear();
	for (const id of ids) selection.toggle("signal", id);
	if (ids.length > 0) selection.select("signal", ids[0], { additive: true });
}

/** A Lead draft opens on a clean pane: whatever Lead or Work Order the details
 * pane was reading is not the draft's. */
function startLeadDraft() {
	selectedLead.value = null;
	selectedWorkOrder.value = null;
	setDraftCells([], null);
	evidence.value = null;
	showClose.value = false;
	newKind.value = "lead";
	rightTab.value = "lead";
	// A Lead's footprint is the first thing it needs, so opening the draft opens
	// the grid: the operator's next move is a cell, not another click on
	// "Paint cells".
	enterPaint();
}

/** Put the map in paint mode, which is the only mode in which the grid is a
 * target. Selecting is what the operator returns to when the geography is done. */
function enterPaint() {
	if (canPaint.value) mapMode.value = "paint";
}

function newLead() {
	// Checked Signals are the draft's evidence; anything else selected is not
	// part of a new hypothesis, so only the Signals survive.
	stageSignals(selectedSignalIDs.value);
	startLeadDraft();
}

function newWorkOrder() {
	clearSelection();
	newKind.value = "work_order";
	rightTab.value = "work_order";
	// A Work Order's footprint is the first thing it needs, so opening the draft
	// opens the grid: the operator's next move is a cell, not another click on
	// "Paint cells".
	enterPaint();
}

/** Forging a Lead from the selection starts the same draft: the Signals that
 * were checked become its evidence. */
function forgeFromSelected() {
	stageSignals(selectedSignals.value.map((signal) => signal.id));
	startLeadDraft();
}

/** Open a Signal's details for review from the Lead form. The create rail and
 * everything the operator has typed into it stay exactly where they are: the
 * fix happens in the other pane and the draft waits. */
function showSignalReview(signalID: number) {
	reviewSignalID.value = signalID;
	leftTab.value = "details";
}

async function reviewSignal(
	id: number,
	body: { provenance_class: string; effect: string; weight?: number },
) {
	reviewing.value = id;
	try {
		const updated = await apiClient.JSONPut<PlanningSignal>(
			`/api/signal/${id}/review`,
			body,
		);
		const index = signals.value.findIndex((signal) => signal.id === id);
		if (index !== -1) signals.value[index] = updated;
		refreshMap();
	} catch (e: any) {
		notify(
			"Review not recorded",
			e?.message ?? "The Signal could not be reviewed.",
		);
	} finally {
		reviewing.value = null;
	}
}

async function bulkReview(body: { provenance_class: string; effect: string }) {
	saving.value = true;
	try {
		for (const signal of selectedSignals.value) {
			await apiClient.JSONPut(`/api/signal/${signal.id}/review`, body);
		}
		await load();
	} catch (e: any) {
		notify(
			"Review not recorded",
			e?.message ?? "A Signal could not be reviewed.",
		);
	} finally {
		saving.value = false;
	}
}

async function bulkCancelWorkOrders(reason: string) {
	saving.value = true;
	try {
		for (const key of selection.multi) {
			if (!key.startsWith("work_order:")) continue;
			const id = Number(key.slice("work_order:".length));
			await apiClient.JSONPut(`/api/work-order/${id}/state`, { reason });
		}
		clearSelection();
		await load();
	} catch (e: any) {
		notify(
			"Work Orders not cancelled",
			e?.message ?? "A Work Order could not be cancelled.",
		);
	} finally {
		saving.value = false;
	}
}

function removeCell(cell: string) {
	const remaining =
		cell === "" ? [] : draftCells.value.filter((c) => c !== cell);
	setDraftCells(remaining, draftRes.value);
}

/** One cell, from a click on the grid. A plain click toggles: the operator
 * paints and un-paints with the same gesture, which is what makes a wrongly
 * clicked neighbour fixable without leaving the map. */
function toggleCell(cell: string, resolution: number) {
	if (!canPaint.value || mapMode.value !== "paint") return;
	const next = new Set(draftCells.value);
	if (next.has(cell)) next.delete(cell);
	else next.add(cell);
	setDraftCells([...next], resolution);
}

/** The cells a drawn area covers — the brush. An area is a gesture, not a
 * toggle: sweeping a parcel adds its cells and never removes one, so a second
 * sweep over the same ground is a no-op rather than an inversion. */
function addCells(cells: string[], resolution: number) {
	if (!canPaint.value || mapMode.value !== "paint") return;
	const next = new Set(draftCells.value);
	for (const cell of cells) next.add(cell);
	setDraftCells([...next], resolution);
}

const placingSignal = ref(false);

/**
 * Place one Signal at the clicked point, then select it: the operator's next act
 * is to review it, and the review control lives in the details pane. The row is
 * born unreviewed with no class, exactly like a derived Signal, so nothing here
 * shortcuts the Lead gate — review is still what makes it production evidence.
 */
async function addSignalAt(longitude: number, latitude: number) {
	if (placingSignal.value) return;
	placingSignal.value = true;
	try {
		const created = await apiClient.JSONPost<PlanningSignal>("/api/signal", {
			latitude,
			longitude,
		});
		mapMode.value = "select";
		await load();
		activeLedger.value = "signals";
		const row = ledgerTabs.value
			.find((tab) => tab.key === "signals")
			?.rows.find((entry) => entry.id === created.id);
		if (row) onLedgerSelect(row, false);
		notify(
			"Signal placed",
			`Signal ${created.id} is unreviewed. Give it a provenance class and an effect to forge a Lead from it.`,
		);
	} catch (e: any) {
		notify(
			"Signal not placed",
			e?.message ?? "The Signal could not be created.",
		);
	} finally {
		placingSignal.value = false;
	}
}

async function saveLead(payload: Record<string, unknown>) {
	saving.value = true;
	try {
		let saved: LeadDetail;
		if (selectedLead.value) {
			const { signals: _signals, ...body } = payload;
			saved = await apiClient.JSONPut<LeadDetail>(
				`/api/lead/${selectedLead.value.id}`,
				body,
			);
		} else {
			saved = await apiClient.JSONPost<LeadDetail>("/api/lead", payload);
		}
		newKind.value = null;
		rightTab.value = null;
		selectedLead.value = saved;
		selectedWorkOrder.value = null;
		selection.select("lead", saved.id);
		setDraftCells(saved.cells, saved.h3_resolution ?? draftRes.value);
		await load();
	} catch (e: any) {
		notify("Lead not saved", e?.message ?? "The Lead could not be saved.");
	} finally {
		saving.value = false;
	}
}

async function attachSignals(role: string) {
	if (!selectedLead.value) return;
	saving.value = true;
	try {
		const saved = await apiClient.JSONPost<LeadDetail>(
			`/api/lead/${selectedLead.value.id}/signal`,
			{
				role,
				signals: stagedSignals.value.map((signal) => ({
					signal_id: signal.id,
					effect: signal.effect ?? "contextual",
				})),
			},
		);
		selectedLead.value = saved;
		selection.select("lead", saved.id);
		await load();
	} catch (e: any) {
		notify("Signals not attached", e?.message ?? "The attachment failed.");
	} finally {
		saving.value = false;
	}
}

async function requestEvidence() {
	if (!selectedLead.value) return;
	loadingEvidence.value = true;
	showClose.value = true;
	try {
		evidence.value = await apiClient.JSONGet<LeadEvidence>(
			`/api/lead/${selectedLead.value.id}/evidence`,
		);
	} catch (e: any) {
		notify(
			"Evidence not read",
			e?.message ?? "Could not load closure evidence.",
		);
	} finally {
		loadingEvidence.value = false;
	}
}

async function closeLead(payload: {
	rationale: string;
	evidence: { kind: string; id: number }[];
}) {
	if (!selectedLead.value) return;
	saving.value = true;
	try {
		const saved = await apiClient.JSONPut<LeadDetail>(
			`/api/lead/${selectedLead.value.id}/state`,
			payload,
		);
		selectedLead.value = saved;
		showClose.value = false;
		evidence.value = null;
		await load();
	} catch (e: any) {
		notify("Lead not closed", e?.message ?? "The closure was refused.");
	} finally {
		saving.value = false;
	}
}

async function saveWorkOrder(payload: Record<string, unknown>) {
	saving.value = true;
	try {
		let saved: WorkOrderDetail;
		if (selectedWorkOrder.value) {
			const body = { ...payload };
			delete body.origin;
			saved = await apiClient.JSONPut<WorkOrderDetail>(
				`/api/work-order/${selectedWorkOrder.value.id}`,
				body,
			);
		} else {
			saved = await apiClient.JSONPost<WorkOrderDetail>(
				"/api/work-order",
				payload,
			);
		}
		newKind.value = null;
		rightTab.value = null;
		selectedWorkOrder.value = saved;
		selectedLead.value = null;
		selection.select("work_order", saved.id);
		setDraftCells(saved.cells, saved.h3_resolution ?? draftRes.value);
		await load();
	} catch (e: any) {
		notify(
			"Work Order not saved",
			e?.message ?? "The Work Order could not be saved.",
		);
	} finally {
		saving.value = false;
	}
}

async function cancelWorkOrder(reason: string) {
	if (!selectedWorkOrder.value) return;
	saving.value = true;
	try {
		const saved = await apiClient.JSONPut<WorkOrderDetail>(
			`/api/work-order/${selectedWorkOrder.value.id}/state`,
			{ reason },
		);
		selectedWorkOrder.value = saved;
		await load();
	} catch (e: any) {
		notify(
			"Work Order not cancelled",
			e?.message ?? "The cancellation was refused.",
		);
	} finally {
		saving.value = false;
	}
}

function setLedgerFraction(value: number) {
	ledgerFraction.value = value;
}

function setLedgerExpanded(expanded: boolean) {
	ledgerFraction.value = expanded ? 1 : 0.34;
}

function formatDate(value: string) {
	return new Date(value).toLocaleDateString();
}
</script>
